import { Edit } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { Button, Show, TabbedShowLayout, useRecordContext, useTranslate } from "react-admin";

import { DataSource } from "../generated/graphql";
import { avatarSize, borderRadius, space, typography } from "../theme/designTokens";
import { SourceSystemChip } from "./fields/SourceSystemChip";
import { LocationDevicesList } from "./LocationDevicesList";
import LocationEdit from "./LocationEdit";
import { LocationInfoPanel } from "./LocationInfoPanel";
import { LocationPatientsList } from "./LocationPatientsList";
import { LocationPractitionersList } from "./LocationPractitionersList";

export interface LocationRecord {
  id: string;
  name?: string;
  type?: string;
  externalId?: string;
  sourceSystem?: DataSource | null;
}

const LocationAvatar = () => {
  return (
    <Box
      component="img"
      src="/location.png"
      alt="Location"
      sx={{
        width: avatarSize.lg,
        height: avatarSize.lg,
        borderRadius: borderRadius.lg,
        objectFit: "cover",
      }}
    />
  );
};

const LocationShowActions = ({ onEditClick }: { onEditClick: () => void }) => {
  const record = useRecordContext<LocationRecord>();
  const translate = useTranslate();
  const isEditable = record?.sourceSystem === DataSource.M4C || !record?.sourceSystem;
  const sourceLabel =
    record?.sourceSystem?.toLowerCase() &&
    translate(`Location.fields.sourceSystem.${record.sourceSystem.toLowerCase()}`);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: `${space.xs}px`, mb: `${space.xs}px` }}>
      <LocationAvatar />
      <SourceSystemChip translatePrefix="Location.fields.sourceSystem" />
      <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: typography.fontWeight.medium }}>
          {record?.name}
        </Typography>
      </Box>
      <Box sx={{ ml: "auto", display: "flex", flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
        {record && (
          <Tooltip
            title={
              isEditable
                ? ""
                : translate("common.editOnlyInSourceSystem", { source: sourceLabel || record?.sourceSystem })
            }
          >
            <span>
              <Button
                label="Wohnbereich bearbeiten"
                color="secondary"
                onClick={isEditable ? onEditClick : undefined}
                disabled={!isEditable}
              >
                <Edit />
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const LocationShow = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const translate = useTranslate();

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
  };

  return (
    <>
      <Show actions={<LocationShowActions onEditClick={handleEditClick} />} title="Wohnbereich">
        <TabbedShowLayout>
          <TabbedShowLayout.Tab label={translate("tabs.information")}>
            <LocationInfoPanel />
          </TabbedShowLayout.Tab>
          <TabbedShowLayout.Tab label={translate("resources.Patient")}>
            <LocationPatientsList />
          </TabbedShowLayout.Tab>
          <TabbedShowLayout.Tab label="Mitarbeiter">
            <LocationPractitionersList />
          </TabbedShowLayout.Tab>
          <TabbedShowLayout.Tab label="Geräte">
            <LocationDevicesList />
          </TabbedShowLayout.Tab>
        </TabbedShowLayout>
      </Show>
      {isEditDialogOpen && <LocationEdit open={isEditDialogOpen} onClose={handleEditClose} />}
    </>
  );
};

export default LocationShow;
